import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { houseService } from "@/services/houseService";
import { adminService } from "@/services/adminService";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import { Avatar } from "@/components/ui";
import { PageFrame } from "@/components/common";
import { HouseAdminProfileForm, HouseHostProfileForm } from "./components";

export default function HouseDetailPage() {
  const { t } = useTranslation("houses");
  const { houseId } = useParams<{ houseId: string }>();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const getCurrentUserId = useAuthStore((s) => s.getCurrentUserId);

  const {
    data: house,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["house", houseId],
    queryFn: () => houseService.getById(houseId!),
    enabled: !!houseId,
  });

  const currentUserId = getCurrentUserId();
  const canAdminUpdateHouse = hasPermission(Permission.HOUSES_UPDATE_ANY);
  const useAdminHouseForm = !!house && canAdminUpdateHouse;
  const managingOthersHouse =
    useAdminHouseForm &&
    !!currentUserId &&
    house?.host_user_id !== currentUserId;

  const canReadHost = hasPermission(Permission.HOSTS_READ_ANY);
  const { data: host } = useQuery({
    queryKey: ["admin", "host", house?.host_user_id],
    queryFn: () => adminService.getHost(house!.host_user_id),
    enabled: managingOthersHouse && canReadHost && !!house?.host_user_id,
  });

  const backFallback = managingOthersHouse ? "/admin/structures" : "/houses";
  const partnerPath =
    managingOthersHouse && house ? `/hosts/${house.host_user_id}` : null;
  const backLabel =
    managingOthersHouse
      ? t("detail.backToHouses", { defaultValue: t("detail.back") })
      : t("detail.back");

  return (
    <PageFrame
      backLink={{
        fallback: backFallback,
        className: "font-normal text-surface-500 hover:text-surface-700",
        children: backLabel,
      }}
      isLoading={isLoading}
      loadingMessage={t("detail.loading")}
      error={!!error || !house}
      errorMessage={t("detail.notFoundMessage")}
      onRetry={() => refetch()}
    >
      {house && useAdminHouseForm ? (
        <HouseAdminProfileForm
          key={house.id}
          house={house}
          houseId={houseId!}
          headerAction={
            partnerPath ? (
              <Link
                to={partnerPath}
                title={t("detail.goToPartner")}
                className="group inline-flex max-w-full items-center gap-3 rounded-xl border border-surface-200 bg-surface-0 py-2 pr-3 pl-2 shadow-xs transition-all duration-200 hover:-translate-y-px hover:border-surface-300 hover:shadow-elevated sm:max-w-72"
              >
                <Avatar
                  size="sm"
                  name={host?.name}
                  avatarLabel={t("detail.goToPartner")}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-surface-500">
                    {t("detail.partnerLabel")}
                  </span>
                  <span className="block truncate text-sm font-medium text-surface-900">
                    {host?.name ?? t("detail.goToPartner")}
                  </span>
                </span>
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-surface-400 transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            ) : undefined
          }
        />
      ) : house ? (
        <HouseHostProfileForm key={house.id} house={house} houseId={houseId!} />
      ) : null}
    </PageFrame>
  );
}
